import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Paper, Grid, CircularProgress, Chip, Divider, Tabs, Tab, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchHeroes } from '../services/dataService';
import countersData from '../data/counters.json';

const TierList = () => {
  const { t } = useLanguage();
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState('diamond_plus');
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const heroesData = await fetchHeroes();
        // Add tier data based on metaRanks from counters.json
        const heroesWithTier = heroesData.map(hero => {
          // Get metaRank values from counters.json
          const diamondPlusValue = countersData[hero.name]?.metaRanks?.diamond_plus || 0;
          const belowDiamondValue = countersData[hero.name]?.metaRanks?.below_diamond || 0;
          
          // Determine tier based on metaRank values
          const getDiamondPlusTier = (value) => {
            if (value === 1.5) return 'S+';
            if (value === 1.25) return 'S';
            if (value === 1.0) return 'A';
            if (value === 0.5) return 'B';
            if (value === 0.25) return 'C';
            if (value === 0.0) return t('new');
          };
          
          const getBelowDiamondTier = (value) => {
            if (value === 1.5) return 'S+';
            if (value === 1.25) return 'S';
            if (value === 1.0) return 'A';
            if (value === 0.5) return 'B';
            if (value === 0.25) return 'C';
            if (value === 0.0) return t('new');
          };
          
          return {
            ...hero,
            diamondPlusTier: getDiamondPlusTier(diamondPlusValue),
            belowDiamondTier: getBelowDiamondTier(belowDiamondValue),
            diamondPlusValue,
            belowDiamondValue
          };
        });
        setHeroes(heroesWithTier);
      } catch (error) {
        console.error("Error loading hero data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [t]);

  const getTierColor = (tier) => {
    switch(tier) {
      case 'S+': return '#ff5722';
      case 'S': return '#f44336';
      case 'A': return '#ffc107';
      case 'B': return '#4caf50';
      case 'C': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'VANGUARD': return '#3498db';
      case 'DUELIST': return '#e74c3c';
      case 'STRATEGIST': return '#2ecc71';
      default: return '#fbdb2b';
    }
  };

  const handleRankChange = (event, newValue) => {
    setSelectedRank(newValue);
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const tierGroups = ['S+', 'S', 'A', 'B', 'C', t('new')].map(tier => ({
    tier,
    heroes: heroes.filter(hero => {
      // First filter by rank
      const matchesRank = selectedRank === 'diamond_plus' 
        ? hero.diamondPlusTier === tier 
        : hero.belowDiamondTier === tier;
      
      // Then filter by role if a role is selected
      return matchesRank && (selectedRole === '' || hero.role === selectedRole);
    })
  }));

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#2b3340',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <CircularProgress sx={{ color: '#fbdb2b' }} size={60} />
        <Typography variant="h5" color="white">
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#2b3340', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            mb: 2,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: '#fbdb2b',
              borderRadius: '2px'
            }
          }}
        >
          {t('tierList')}
        </Typography>
        
        <Box sx={{ width: '100%', mb: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tabs
            value={selectedRank}
            onChange={handleRankChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#fbdb2b',
              },
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-selected': {
                  color: '#fbdb2b',
                  fontWeight: 'bold',
                },
              },
            }}
          >
            <Tab value="diamond_plus" label="Diamond+" />
            <Tab value="below_diamond" label="Below Diamond" />
          </Tabs>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel 
              id="role-select-label"
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': {
                  color: '#fbdb2b'
                }
              }}
            >
              {t('filterByRole')}
            </InputLabel>
            <Select
              labelId="role-select-label"
              value={selectedRole}
              label={t('filterByRole')}
              onChange={handleRoleChange}
              sx={{ 
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#fbdb2b',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                '.MuiSvgIcon-root': {
                  color: 'rgba(255,255,255,0.7)',
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#2b3340',
                    color: 'white',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: 'rgba(251, 219, 43, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(251, 219, 43, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(251, 219, 43, 0.3)',
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="">
                <em>{t('allRoles')}</em>
              </MenuItem>
              <MenuItem value="VANGUARD">{t('vanguard')}</MenuItem>
              <MenuItem value="DUELIST">{t('duelist')}</MenuItem>
              <MenuItem value="STRATEGIST">{t('strategist')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            backgroundColor: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              mb: 2
            }}
          >
            {t('heroTierRankings')}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              mb: 3
            }}
          >
            {t('tierListDescription')}
          </Typography>

          {tierGroups.map(group => (
            <Box key={group.tier} sx={{ mb: 4 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  gap: 2
                }}
              >
                <Chip 
                  label={`${group.tier}`} 
                  sx={{ 
                    backgroundColor: getTierColor(group.tier),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    px: 1
                  }}
                />
                <Divider 
                  sx={{ 
                    flexGrow: 1, 
                    borderColor: 'rgba(255,255,255,0.1)' 
                  }}
                />
              </Box>
              
              <Grid container spacing={0.5}>
                {group.heroes.map(hero => (
                  <Grid item xs={3} sm={2} md={1.5} lg={1} key={hero.id}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${getTierColor(group.tier)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: `0 5px 15px ${getTierColor(group.tier)}30`
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <img
                          src={hero.image}
                          alt={hero.name}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                          }}
                        />
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            right: 0, 
                            backgroundColor: getRoleColor(hero.role),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.6rem',
                            padding: '1px 4px',
                            borderBottomLeftRadius: '6px'
                          }}
                        >
                          {hero.role === 'VANGUARD' ? t('vanguard') : 
                           hero.role === 'DUELIST' ? t('duelist') : 
                           hero.role === 'STRATEGIST' ? t('strategist') : hero.role}
                        </Box>
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            backgroundColor: getTierColor(group.tier),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.6rem',
                            padding: '1px 4px',
                            borderBottomRightRadius: '6px'
                          }}
                        >
                          {group.tier}
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ 
                          display: 'block', 
                          py: 0.5,
                          px: 0.5,
                          color: 'white',
                          fontWeight: 'medium',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {hero.name}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                {group.heroes.length === 0 && (
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center', 
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: 2,
                        border: '1px dashed rgba(255,255,255,0.2)'
                      }}
                    >
                      <Typography variant="body2" color="rgba(255,255,255,0.5)">
                        {t('noHeroesInTier')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {t('dataUpdated')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
            {t('copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TierList;